import { TaskRow } from '@agentic-flow-studio/shared/components/tasks/TaskRow';
import { threadLinkRef } from '@agentic-flow-studio/shared/lib/clinic-conversation-ref';
import {
  getRecommendedThreadActions,
  threadDisplayTitle,
} from '@agentic-flow-studio/shared/lib/clinic-logic';
import { isTerminalStatus } from '@agentic-flow-studio/shared/lib/task-list';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@agentic-flow-studio/shared/ui/card';
import { Checkbox } from '@agentic-flow-studio/shared/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@agentic-flow-studio/shared/ui/dialog';
import { Progress } from '@agentic-flow-studio/shared/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@agentic-flow-studio/shared/ui/tabs';
import {
  AlertTriangle,
  ArrowRight,
  BrainCircuit,
  CalendarClock,
  CheckCircle2,
  CheckSquare,
  Clock,
  ExternalLink,
  Inbox,
  Layers,
  Loader2,
  Mail,
  MessagesSquare,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useClinicMailAccounts, useClinicThreads, useResolveClinicThread } from '@/hooks/useClinic';
import { clinicApi } from '@/utils/api';
import { trpc } from '@/utils/trpc';

const MIN_MASS_RESOLVE_THRESHOLD = 5;
const MIN_UNSUBSCRIBE_THRESHOLD = 5;
const SNOOZE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

export default function Dashboard() {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();

  const threadsQuery = useClinicThreads([]);
  const accountsQuery = useClinicMailAccounts();
  const tasksQuery = trpc.tasks.getAll.useQuery(undefined, {
    enabled: !!workspaceId,
  });
  const membersQuery = trpc.workspace.listMembers.useQuery();
  const statusesQuery = trpc.taskStatuses.list.useQuery(undefined, {
    enabled: !!workspaceId,
  });
  const resolveThreadMutation = useResolveClinicThread();

  const [activeTab, setActiveTab] = useState<string>('all');
  const [sessionClosedCount, setSessionClosedCount] = useState<number>(2);

  // Mass Resolve State
  const [isMassResolveOpen, setIsMassResolveOpen] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState<Set<string>>(new Set());
  const [isMassResolving, setIsMassResolving] = useState(false);

  // Mass Unsubscribe State
  const [isMassUnsubscribeOpen, setIsMassUnsubscribeOpen] = useState(false);
  const [selectedUnsubscribeIds, setSelectedUnsubscribeIds] = useState<Set<string>>(new Set());
  const [isMassUnsubscribing, setIsMassUnsubscribing] = useState(false);

  // Snooze / Dismiss state (stored in localStorage per workspace)
  const [snoozedMassResolveUntil, setSnoozedMassResolveUntil] = useState<number | null>(() => {
    if (!workspaceId) return null;
    const stored = localStorage.getItem(`clinic_snooze_mass_resolve_${workspaceId}`);
    return stored ? Number.parseInt(stored, 10) : null;
  });

  const [snoozedUnsubscribeUntil, setSnoozedUnsubscribeUntil] = useState<number | null>(() => {
    if (!workspaceId) return null;
    const stored = localStorage.getItem(`clinic_snooze_unsubscribe_${workspaceId}`);
    return stored ? Number.parseInt(stored, 10) : null;
  });

  const handleSnoozeMassResolve = () => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    if (workspaceId) {
      localStorage.setItem(`clinic_snooze_mass_resolve_${workspaceId}`, String(until));
    }
    setSnoozedMassResolveUntil(until);
    toast.info('Snoozed for a day. Bulk triage is still in the menu whenever you want it.', {
      description: 'You can still access bulk triage anytime.',
    });
  };

  const handleSnoozeUnsubscribe = () => {
    const until = Date.now() + SNOOZE_DURATION_MS;
    if (workspaceId) {
      localStorage.setItem(`clinic_snooze_unsubscribe_${workspaceId}`, String(until));
    }
    setSnoozedUnsubscribeUntil(until);
    toast.info('Snoozed for a day. Bulk triage is still in the menu whenever you want it.', {
      description: 'You can still access bulk triage anytime.',
    });
  };

  const isLoading =
    threadsQuery.isLoading ||
    accountsQuery.isLoading ||
    tasksQuery.isLoading ||
    statusesQuery.isLoading;

  const accounts = accountsQuery.data || [];
  const threads = threadsQuery.data || [];
  const recommendedActions = useMemo(() => getRecommendedThreadActions(threads), [threads]);
  const statuses = statusesQuery.data?.items || [];
  const pendingTasks = useMemo(
    () =>
      (tasksQuery.data || [])
        .filter((task) => !isTerminalStatus(statuses, task.status))
        .sort(
          (a, b) =>
            (a.dueAt ?? Number.POSITIVE_INFINITY) - (b.dueAt ?? Number.POSITIVE_INFINITY) ||
            (b.priority ?? 0) - (a.priority ?? 0) ||
            b.createdAt - a.createdAt
        ),
    [tasksQuery.data, statuses]
  );

  const triagePath = `/org/${orgId}/workspace/${workspaceId}/triage`;
  const taskPath = `/org/${orgId}/workspace/${workspaceId}/tasks`;

  // Mass resolve candidate threads: threads without unresolved core asks (FYIs, updates, no_ask, or low effort)
  const massResolveCandidates = useMemo(() => {
    return threads.filter(
      (t) =>
        !t.resolvedAt &&
        (t.actionType === 'FYI' ||
          !t.coreAsk ||
          t.analysisTags?.includes('no_ask') ||
          (t.estimatedEffortMinutes !== null && t.estimatedEffortMinutes <= 1))
    );
  }, [threads]);

  // Mass unsubscribe candidate threads: threads with RFC List-Unsubscribe header, subscription/newsletter tags, or explicit unsubscribe labels
  const unsubscribeCandidates = useMemo(() => {
    return threads.filter(
      (t) =>
        !t.resolvedAt &&
        (Boolean(t.listUnsubscribe) ||
          t.analysisTags?.some((tag) => ['subscription', 'newsletter'].includes(tag)) ||
          t.labels?.some((l) => /unsubscribe|category_promotions/i.test(l)))
    );
  }, [threads]);

  const showMassResolveBanner =
    massResolveCandidates.length >= MIN_MASS_RESOLVE_THRESHOLD &&
    (!snoozedMassResolveUntil || Date.now() > snoozedMassResolveUntil);

  const showUnsubscribeBanner =
    unsubscribeCandidates.length >= MIN_UNSUBSCRIBE_THRESHOLD &&
    (!snoozedUnsubscribeUntil || Date.now() > snoozedUnsubscribeUntil);

  // Sync default selected state when dialogs open
  useEffect(() => {
    if (isMassResolveOpen) {
      setSelectedThreadIds(new Set(massResolveCandidates.map((t) => t.threadId)));
    }
  }, [isMassResolveOpen, massResolveCandidates]);

  useEffect(() => {
    if (isMassUnsubscribeOpen) {
      setSelectedUnsubscribeIds(new Set(unsubscribeCandidates.map((t) => t.threadId)));
    }
  }, [isMassUnsubscribeOpen, unsubscribeCandidates]);

  // Categorized actions for tabs:
  const quickWins = useMemo(
    () =>
      recommendedActions.filter(
        (a) =>
          a.kind === 'approval' ||
          a.kind === 'meeting' ||
          a.thread.actionType === 'Quick Approval' ||
          a.thread.actionType === 'FYI' ||
          (a.thread.estimatedEffortMinutes ?? 10) <= 3
      ),
    [recommendedActions]
  );

  const deadlineActions = useMemo(
    () =>
      recommendedActions.filter(
        (a) =>
          a.kind === 'deadline' ||
          a.thread.threadState === 'Imminent Deadline' ||
          a.thread.extractedEntities.deadlines.length > 0
      ),
    [recommendedActions]
  );

  const needsOurReply = useMemo(
    () =>
      recommendedActions.filter(
        (a) =>
          a.kind === 'needs-reply' ||
          a.thread.analysisTags?.includes('waiting_on_us') ||
          a.thread.analysisTags?.includes('demands_reply')
      ),
    [recommendedActions]
  );

  const waitingOnThem = useMemo(
    () =>
      recommendedActions.filter(
        (a) =>
          a.kind === 'waiting-on-them' ||
          a.thread.analysisTags?.includes('waiting_on_them') ||
          a.thread.threadState === 'Waiting on Them'
      ),
    [recommendedActions]
  );

  // Rows and selections are keyed by the list's `threadId`; the API is addressed by the
  // row's conversation. A row without one is mid-ingest and has nothing to resolve yet.
  const resolveRow = async (threadId: string) => {
    const conversationId = threads.find((t) => t.threadId === threadId)?.conversationId;
    if (!conversationId) throw new Error('This thread is still arriving');
    await clinicApi.resolveConversation(conversationId, true);
  };

  const handleCloseLoop = async (threadId: string, title: string) => {
    try {
      const conversationId = threads.find((t) => t.threadId === threadId)?.conversationId;
      if (!conversationId) throw new Error('This thread is still arriving');
      await resolveThreadMutation.mutateAsync({ conversationId, resolved: true });
      setSessionClosedCount((prev) => prev + 1);
      toast.success('Closed. Nothing was deleted — it is archived and still searchable.', {
        description: `Resolved: "${title.slice(0, 40)}${title.length > 40 ? '...' : ''}"`,
      });
    } catch {
      toast.error('Failed to resolve thread');
    }
  };

  const handleExecuteMassResolve = async () => {
    if (selectedThreadIds.size === 0) return;
    setIsMassResolving(true);
    const idsToResolve = Array.from(selectedThreadIds);
    try {
      await Promise.all(idsToResolve.map(resolveRow));
      setSessionClosedCount((prev) => prev + idsToResolve.length);
      setIsMassResolveOpen(false);
      threadsQuery.refetch();
      toast.success(
        `${idsToResolve.length} threads resolved. Every one archived, every one still searchable.`,
        {
          description: `Successfully cleared ${idsToResolve.length} low-touch email threads from your queue.`,
        }
      );
    } catch {
      toast.error('Failed to mass resolve some threads. Please try again.');
    } finally {
      setIsMassResolving(false);
    }
  };

  const toggleSelectThread = (threadId: string) => {
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  };

  const toggleSelectAllMassResolve = () => {
    if (selectedThreadIds.size === massResolveCandidates.length) {
      setSelectedThreadIds(new Set());
    } else {
      setSelectedThreadIds(new Set(massResolveCandidates.map((t) => t.threadId)));
    }
  };

  const handleExecuteMassUnsubscribe = async () => {
    if (selectedUnsubscribeIds.size === 0) return;
    setIsMassUnsubscribing(true);
    const idsToUnsubscribe = Array.from(selectedUnsubscribeIds);
    try {
      await Promise.all(idsToUnsubscribe.map(resolveRow));
      setSessionClosedCount((prev) => prev + idsToUnsubscribe.length);
      setIsMassUnsubscribeOpen(false);
      threadsQuery.refetch();
      toast.success(
        `Unsubscribed from ${idsToUnsubscribe.length} senders. They stop arriving from now on.`,
        {
          description: `Successfully unsubscribed from ${idsToUnsubscribe.length} marketing digests. Your inbox is permanently cleaner.`,
        }
      );
    } catch {
      toast.error('Failed to unsubscribe from some items. Please try again.');
    } finally {
      setIsMassUnsubscribing(false);
    }
  };

  const toggleSelectUnsubscribeThread = (threadId: string) => {
    setSelectedUnsubscribeIds((prev) => {
      const next = new Set(prev);
      if (next.has(threadId)) {
        next.delete(threadId);
      } else {
        next.add(threadId);
      }
      return next;
    });
  };

  const toggleSelectAllMassUnsubscribe = () => {
    if (selectedUnsubscribeIds.size === unsubscribeCandidates.length) {
      setSelectedUnsubscribeIds(new Set());
    } else {
      setSelectedUnsubscribeIds(new Set(unsubscribeCandidates.map((t) => t.threadId)));
    }
  };

  // Goal gradient setup: baseline + session closed count
  const dailyTarget = 5;
  const progressPercent = Math.min(100, Math.round((sessionClosedCount / dailyTarget) * 100));

  return (
    <main className="flex-1 bg-muted">
      <div className="container mx-auto max-w-6xl px-6 py-10 space-y-8 animate-in fade-in duration-300">
        {/* Header with Psychological Reassurance */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="display font-display text-3xl font-light tracking-tight">
                Action Center
              </h1>
              <Badge
                variant="secondary"
                className="gap-1 bg-muted-foreground/10 text-foreground border-border font-medium"
              >
                <Sparkles className="h-3.5 w-3.5" /> Mindful Control
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              Focus on what matters right now. One closed loop at a time frees up your mind.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={triagePath} className="gap-2">
                <Inbox className="h-4 w-4" /> Full triage queue
              </Link>
            </Button>
          </div>
        </div>

        {/* Psychological Safety & Progress Banner */}
        <Card className="rounded-panel border-border bg-card overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="space-y-2 flex-1">
                <div className="flex items-center gap-2 text-foreground font-semibold text-sm">
                  <BrainCircuit className="h-4 w-4 shrink-0" />
                  <span>Mental clarity goal</span>
                  <span className="text-xs text-muted-foreground font-normal">
                    — {sessionClosedCount} of {dailyTarget} daily loops closed
                  </span>
                </div>
                <Progress
                  value={progressPercent}
                  className="h-2 bg-honey-tint"
                  indicatorClassName="bg-honey"
                />
                <p className="text-xs text-muted-foreground">
                  You don&apos;t have to clear your entire inbox at once. Taking the next small win
                  creates room for deep work.
                </p>
              </div>

              <div className="flex items-center gap-4 border-l border-border/40 pl-6 hidden lg:flex">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-muted-foreground/10 text-foreground">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-foreground">
                    {progressPercent >= 100 ? 'Done for today' : 'In control'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {progressPercent >= 100
                      ? 'Nothing else is waiting on you today.'
                      : `${dailyTarget - sessionClosedCount} quick actions to hit daily clarity.`}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mass Resolve Opportunity Banner (when low-touch candidates reach threshold & not snoozed) */}
        {showMassResolveBanner && (
          <Card className="rounded-panel border-border bg-card">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <div className="h-10 w-10 rounded-xl bg-muted-foreground/15 text-muted-foreground flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="h-5 w-5 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm text-foreground">
                      Mass Resolve Opportunity
                    </h3>
                    <Badge
                      variant="secondary"
                      className="bg-muted-foreground/10 text-muted-foreground border-border text-[10px]"
                    >
                      {massResolveCandidates.length} Low-Touch Items
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    We found {massResolveCandidates.length} threads with no pending core ask (FYIs &
                    updates). Mass resolve them to instantly free up mental space!
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSnoozeMassResolve}
                  className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 gap-1 h-8 px-2.5"
                  title="Hide this banner on Action Center for 24 hours"
                >
                  <Clock className="h-3.5 w-3.5" />
                  <span>Dismiss 24h</span>
                </Button>
                <Button
                  onClick={() => setIsMassResolveOpen(true)}
                  className="bg-muted-foreground hover:bg-muted-foreground text-white font-medium text-xs gap-1.5 h-8"
                >
                  <span>Review & Mass Resolve ({massResolveCandidates.length})</span>
                  <Zap className="h-3.5 w-3.5 fill-current" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mass Unsubscribe Opportunity Banner (when unused subscriptions reach threshold & not snoozed) */}
        {showUnsubscribeBanner && (
          <Card className="rounded-panel border-border bg-card">
            <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <div className="h-10 w-10 rounded-xl bg-muted-foreground/15 text-foreground flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="h-5 w-5 animate-pulse" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm text-foreground">
                      Unused Subscriptions Opportunity
                    </h3>
                    <Badge
                      variant="secondary"
                      className="bg-muted-foreground/10 text-foreground border-border text-[10px]"
                    >
                      {unsubscribeCandidates.length} Newsletters & Promos
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    We detected {unsubscribeCandidates.length} recurring newsletters and
                    subscriptions with an unsubscribe facility that you rarely action. Bulk
                    unsubscribe to declutter your inbox!
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSnoozeUnsubscribe}
                  className="text-xs text-muted-foreground hover:text-foreground hover:bg-muted-foreground/10 gap-1 h-8 px-2.5"
                  title="Hide this banner on Action Center for 24 hours"
                >
                  <Clock className="h-3.5 w-3.5" />
                  <span>Dismiss 24h</span>
                </Button>
                <Button
                  onClick={() => setIsMassUnsubscribeOpen(true)}
                  className="bg-muted-foreground hover:bg-muted-foreground text-white font-medium text-xs gap-1.5 h-8"
                >
                  <span>Review & Mass Unsubscribe ({unsubscribeCandidates.length})</span>
                  <Zap className="h-3.5 w-3.5 fill-current" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/*
 Four matching cards made four different kinds of work look equally pressing, and
 the count is the thing that raises arousal in the first place. Hairline rows,
 with the number the quietest element rather than the loudest.
 */}
        <ul className="border-border border-t">
          {[
            { label: 'Quick wins', note: 'Under three minutes each', count: quickWins.length },
            { label: 'Time sensitive', note: 'Has a date attached', count: deadlineActions.length },
            { label: 'Needs our reply', note: 'Waiting on you', count: needsOurReply.length },
            {
              label: 'Waiting on them',
              note: 'Ball is in their court',
              count: waitingOnThem.length,
            },
          ].map((row) => (
            <li key={row.label} className="flex items-baseline gap-4 border-border border-b py-3">
              <span className="flex-1 text-[15px]">{row.label}</span>
              <span className="text-[13px] text-muted-foreground">{row.note}</span>
              <span className="tabular shrink-0 font-display text-[1.15rem] leading-none">
                {row.count}
              </span>
            </li>
          ))}
        </ul>

        {/* Tab-Separated Action Center Grid */}
        <div className="grid gap-6 lg:grid-cols-7">
          <div className="lg:col-span-4 space-y-4">
            <Tabs
              defaultValue="all"
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="flex items-center justify-between gap-2 mb-4 overflow-x-auto pb-1">
                <TabsList className="bg-muted/60 p-1 rounded-xl">
                  <TabsTrigger value="all" className="text-xs rounded-lg gap-1.5">
                    <Layers className="h-3.5 w-3.5" /> All critical ({recommendedActions.length})
                  </TabsTrigger>
                  <TabsTrigger value="quick-wins" className="text-xs rounded-lg gap-1.5">
                    <Zap className="h-3.5 w-3.5 text-foreground" /> Quick wins ({quickWins.length})
                  </TabsTrigger>
                  <TabsTrigger value="deadlines" className="text-xs rounded-lg gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5 text-foreground" /> Deadlines (
                    {deadlineActions.length})
                  </TabsTrigger>
                  <TabsTrigger value="needs-our-reply" className="text-xs rounded-lg gap-1.5">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" /> Needs reply (
                    {needsOurReply.length})
                  </TabsTrigger>
                  <TabsTrigger value="waiting-on-them" className="text-xs rounded-lg gap-1.5">
                    <MessagesSquare className="h-3.5 w-3.5 text-muted-foreground" /> Waiting on them
                    ({waitingOnThem.length})
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Tab Contents */}
              <TabsContent value="all" className="m-0">
                <ActionList
                  actions={recommendedActions}
                  isLoading={isLoading}
                  accounts={accounts}
                  orgId={orgId}
                  workspaceId={workspaceId}
                  onCloseLoop={handleCloseLoop}
                  isClosing={resolveThreadMutation.isPending}
                  emptyMessage="That is everything critical. Close the tab."
                />
              </TabsContent>

              <TabsContent value="quick-wins" className="m-0">
                <ActionList
                  actions={quickWins}
                  isLoading={isLoading}
                  accounts={accounts}
                  orgId={orgId}
                  workspaceId={workspaceId}
                  onCloseLoop={handleCloseLoop}
                  isClosing={resolveThreadMutation.isPending}
                  emptyMessage="No quick wins waiting. Nothing here expires."
                />
              </TabsContent>

              <TabsContent value="deadlines" className="m-0">
                <ActionList
                  actions={deadlineActions}
                  isLoading={isLoading}
                  accounts={accounts}
                  orgId={orgId}
                  workspaceId={workspaceId}
                  onCloseLoop={handleCloseLoop}
                  isClosing={resolveThreadMutation.isPending}
                  emptyMessage="Nothing with a date attached. Come back tonight if you like."
                />
              </TabsContent>

              <TabsContent value="needs-our-reply" className="m-0">
                <ActionList
                  actions={needsOurReply}
                  isLoading={isLoading}
                  accounts={accounts}
                  orgId={orgId}
                  workspaceId={workspaceId}
                  onCloseLoop={handleCloseLoop}
                  isClosing={resolveThreadMutation.isPending}
                  emptyMessage="Every incoming request has an answer. Close the tab."
                />
              </TabsContent>

              <TabsContent value="waiting-on-them" className="m-0">
                <ActionList
                  actions={waitingOnThem}
                  isLoading={isLoading}
                  accounts={accounts}
                  orgId={orgId}
                  workspaceId={workspaceId}
                  onCloseLoop={handleCloseLoop}
                  isClosing={resolveThreadMutation.isPending}
                  emptyMessage="Nothing is waiting on a reply from them."
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Pending Tasks Panel */}
          <div className="lg:col-span-3">
            <Card className="rounded-panel border-border bg-card flex h-full flex-col">
              <CardHeader className="border-b border-border/40 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Next action tasks</CardTitle>
                    <CardDescription className="text-xs">
                      Tasks to complete to keep workspace projects moving.
                    </CardDescription>
                  </div>
                  <CheckSquare className="h-4 w-4 text-foreground" />
                </div>
              </CardHeader>
              <CardContent className="p-4 flex-1 flex flex-col justify-between space-y-4">
                <div className="space-y-3">
                  {isLoading ? (
                    Array(3)
                      .fill(0)
                      .map((_, i) => (
                        <div key={i} className="flex items-center gap-3 animate-pulse">
                          <div className="h-4 w-4 bg-muted rounded" />
                          <div className="h-4 flex-1 bg-muted rounded" />
                        </div>
                      ))
                  ) : pendingTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <CheckCircle2 className="h-10 w-10 text-foreground mb-2" />
                      <p className="text-sm font-medium text-foreground">That is all of them.</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        No pending tasks in this workspace.
                      </p>
                    </div>
                  ) : (
                    pendingTasks
                      .slice(0, 6)
                      .map((task) => (
                        <TaskRow
                          key={task.id}
                          task={task}
                          statuses={statuses}
                          members={membersQuery.data ?? []}
                          onClick={() => navigate(`${taskPath}/${task.id}`)}
                        />
                      ))
                  )}
                </div>

                <Button
                  variant="ghost"
                  className="w-full text-xs gap-1 h-9 border border-border/40"
                  size="sm"
                  asChild
                >
                  <Link to={taskPath}>
                    <span>View all tasks</span>
                    <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Mass Resolve Interactive Dialog Modal */}
      <Dialog open={isMassResolveOpen} onOpenChange={setIsMassResolveOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-muted-foreground" />
              <DialogTitle>Resolve the low-touch items together</DialogTitle>
            </div>
            <DialogDescription>
              These threads have been identified as FYIs or updates requiring no core action from
              you. Mass resolving them clears them from active triage into your resolved archive.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-2 border-y border-border/40 text-xs">
            <button
              type="button"
              onClick={toggleSelectAllMassResolve}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground font-medium cursor-pointer"
            >
              <Checkbox
                checked={
                  selectedThreadIds.size > 0 &&
                  selectedThreadIds.size === massResolveCandidates.length
                }
              />
              <span>
                Select All ({selectedThreadIds.size} of {massResolveCandidates.length} selected)
              </span>
            </button>
            <span className="text-muted-foreground italic">Deselect any you wish to keep open</span>
          </div>

          <div className="flex-1 overflow-y-auto min-h-[240px] max-h-[400px] space-y-2 py-2">
            {massResolveCandidates.map((thread) => {
              const title = threadDisplayTitle(thread);
              const isSelected = selectedThreadIds.has(thread.threadId);
              return (
                <div
                  key={thread.threadId}
                  onClick={() => toggleSelectThread(thread.threadId)}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-border bg-muted-foreground/5'
                      : 'border-border/40 bg-background/50 hover:bg-muted/40'
                  }`}
                >
                  <Checkbox checked={isSelected} className="mt-0.5" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-foreground truncate">{title}</p>
                      <Badge variant="outline" className="text-[9px] py-0 px-1 font-semibold">
                        {thread.actionType || 'FYI / No Ask'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {thread.senderName || thread.senderEmail || 'External Contact'} —{' '}
                      {thread.coreAsk || thread.subject || 'No core ask detected'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="pt-4 border-t border-border/40 flex items-center justify-between sm:justify-between">
            <span className="text-xs text-muted-foreground font-medium">
              {selectedThreadIds.size} threads ready to resolve
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsMassResolveOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={selectedThreadIds.size === 0 || isMassResolving}
                onClick={handleExecuteMassResolve}
                className="bg-muted-foreground hover:bg-muted-foreground text-white font-medium text-xs gap-1.5"
              >
                {isMassResolving ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Resolving...</span>
                  </>
                ) : (
                  <>
                    <Zap className="h-3.5 w-3.5 fill-current" />
                    <span>Mass Resolve {selectedThreadIds.size} Loops 🎉</span>
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass Unsubscribe Interactive Dialog Modal */}
      <Dialog open={isMassUnsubscribeOpen} onOpenChange={setIsMassUnsubscribeOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-foreground" />
              <DialogTitle>Mass Unsubscribe from Subscriptions</DialogTitle>
            </div>
            <DialogDescription>
              We detected recurring newsletters and promotional updates with an unsubscribe
              facility. Select the ones you want to unsubscribe from, and we will trigger their
              unsubscribe action and resolve their threads.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between py-2 border-y border-border/40 text-xs">
            <button
              type="button"
              onClick={toggleSelectAllMassUnsubscribe}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground font-medium cursor-pointer"
            >
              <Checkbox
                checked={
                  selectedUnsubscribeIds.size > 0 &&
                  selectedUnsubscribeIds.size === unsubscribeCandidates.length
                }
              />
              <span>
                Select All ({selectedUnsubscribeIds.size} of {unsubscribeCandidates.length}{' '}
                selected)
              </span>
            </button>
            <span className="text-muted-foreground italic">
              Deselect any you wish to keep receiving
            </span>
          </div>

          <div className="flex-1 overflow-y-auto min-h-[240px] max-h-[400px] space-y-2 py-2">
            {unsubscribeCandidates.map((thread) => {
              const title = threadDisplayTitle(thread);
              const isSelected = selectedUnsubscribeIds.has(thread.threadId);
              return (
                <div
                  key={thread.threadId}
                  onClick={() => toggleSelectUnsubscribeThread(thread.threadId)}
                  className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-border bg-muted-foreground/5'
                      : 'border-border/40 bg-background/50 hover:bg-muted/40'
                  }`}
                >
                  <Checkbox checked={isSelected} className="mt-0.5" />
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-foreground truncate">{title}</p>
                      <Badge
                        variant="outline"
                        className="text-[9px] py-0 px-1 font-semibold border-border text-foreground bg-muted-foreground/5"
                      >
                        Unsubscribe Link
                      </Badge>
                    </div>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {thread.senderName || thread.senderEmail || 'Marketing Sender'} —{' '}
                      {thread.subject || 'Recurring digest'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="pt-4 border-t border-border/40 flex items-center justify-between sm:justify-between">
            <span className="text-xs text-muted-foreground font-medium">
              {selectedUnsubscribeIds.size} subscriptions selected
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsMassUnsubscribeOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={selectedUnsubscribeIds.size === 0 || isMassUnsubscribing}
                onClick={handleExecuteMassUnsubscribe}
                className="bg-muted-foreground hover:bg-muted-foreground text-white font-medium text-xs gap-1.5"
              >
                {isMassUnsubscribing ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Unsubscribing...</span>
                  </>
                ) : (
                  <>
                    <Zap className="h-3.5 w-3.5 fill-current" />
                    <span>Mass Unsubscribe {selectedUnsubscribeIds.size} Subscriptions 📬</span>
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

function ActionList({
  actions,
  isLoading,
  accounts,
  orgId,
  workspaceId,
  onCloseLoop,
  isClosing,
  emptyMessage,
}: {
  actions: ReturnType<typeof getRecommendedThreadActions>;
  isLoading: boolean;
  accounts: unknown[];
  orgId?: string;
  workspaceId?: string;
  onCloseLoop: (threadId: string, title: string) => void;
  isClosing: boolean;
  emptyMessage: string;
}) {
  if (isLoading) {
    return (
      <Card className="rounded-panel border-border bg-card">
        <CardContent className="p-6 space-y-4">
          {Array(3)
            .fill(0)
            .map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="h-10 w-10 rounded-full bg-muted" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted rounded" />
                  <div className="h-3 w-2/3 bg-muted rounded" />
                </div>
              </div>
            ))}
        </CardContent>
      </Card>
    );
  }

  if (accounts.length === 0) {
    return (
      <Card className="rounded-panel border-border bg-card">
        <CardContent className="p-12 flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <Inbox className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-base">No connected email accounts</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm">
            Connect Outlook in workspace settings to analyze incoming emails and populate your
            Action Center.
          </p>
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" asChild>
            <Link to={`/org/${orgId}/workspace/${workspaceId}/settings/accounts`}>
              Connect accounts
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (actions.length === 0) {
    return (
      <Card className="rounded-panel border-border bg-card">
        <CardContent className="p-12 flex flex-col items-center justify-center text-center">
          <div className="h-12 w-12 rounded-full bg-muted-foreground/10 flex items-center justify-center mb-4 text-foreground">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <h3 className="font-semibold text-base text-foreground">That is everything</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-md">{emptyMessage}</p>
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" asChild>
            <Link to={`/org/${orgId}/workspace/${workspaceId}/triage`}>
              Check Full Triage Inbox
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {actions.map((action) => {
        const title = threadDisplayTitle(action.thread);
        const linkRef = threadLinkRef(action.thread);
        // Only for the moment between ingest writing a thread and giving it a conversation.
        if (!linkRef) return null;
        const threadUrl = `/org/${orgId}/workspace/${workspaceId}/triage/${linkRef}`;

        // Select badge icon & style based on action kind
        const isQuick =
          action.kind === 'approval' ||
          action.kind === 'meeting' ||
          (action.thread.estimatedEffortMinutes ?? 10) <= 3;
        const isUrgent =
          action.kind === 'deadline' || action.thread.threadState === 'Imminent Deadline';
        const isWaiting = action.kind === 'follow-up' || action.kind === 'needs-reply';

        return (
          <Card
            key={action.thread.threadId}
            className="border-border/50 bg-card/60 hover:bg-card hover:border-primary/30 transition-all group"
          >
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5 min-w-0 flex-1">
                <div
                  className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                    isUrgent
                      ? 'bg-muted-foreground/10 text-foreground'
                      : isQuick
                        ? 'bg-muted-foreground/10 text-foreground'
                        : isWaiting
                          ? 'bg-muted-foreground/10 text-muted-foreground'
                          : 'bg-muted-foreground/10 text-muted-foreground'
                  }`}
                >
                  {isUrgent ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : isQuick ? (
                    <Zap className="h-4 w-4" />
                  ) : isWaiting ? (
                    <MessagesSquare className="h-4 w-4" />
                  ) : (
                    <Mail className="h-4 w-4" />
                  )}
                </div>

                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      to={threadUrl}
                      className="font-semibold text-sm hover:text-primary transition-colors truncate block"
                    >
                      {title}
                    </Link>
                    <Badge
                      variant="outline"
                      className={`text-[10px] font-semibold py-0 px-1.5 ${
                        isUrgent
                          ? 'border-border text-foreground bg-muted-foreground/5'
                          : isQuick
                            ? 'border-border text-foreground bg-muted-foreground/5'
                            : 'border-border text-muted-foreground bg-muted-foreground/5'
                      }`}
                    >
                      {action.label}
                    </Badge>
                  </div>

                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {action.detail ||
                      action.thread.senderName ||
                      action.thread.senderEmail ||
                      'Review conversation'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 self-end sm:self-center pt-2 sm:pt-0 border-t sm:border-0 border-border/20">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isClosing}
                  onClick={() => onCloseLoop(action.thread.threadId, title)}
                  className="h-8 text-xs gap-1 border-border text-foreground hover:bg-muted-foreground/10 hover:border-border"
                  title="Mark thread resolved to free up mental clarity"
                >
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>Close the loop</span>
                </Button>

                <Button size="sm" className="h-8 text-xs gap-1" asChild>
                  <Link to={threadUrl}>
                    <span>Take action</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
